import { base64ToBytes, bytesToBase64, generateRandomBytes } from "./masterKey";

// Derive an AES-GCM key from a password and salt using PBKDF2
async function derivePasswordKey(
  password: string,
  salt: Uint8Array
): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const baseKey = await crypto.subtle.importKey(
    "raw",
    enc.encode(password),
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt as BufferSource,
      iterations: 100000,
      hash: "SHA-256",
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export interface EncryptedKeystore {
  salt: string; // base64
  iv: string; // base64
  ciphertext: string; // base64
  publicKey: string; // base64 uncompressed SEC1
}

/**
 * Generates an Adviser P-256 ECDH key pair and returns the public key
 * along with the AES-GCM encrypted PKCS#8 private key JSON keystore.
 */
export async function generateEscrowKeystore(password: string): Promise<{
  publicKeyBase64: string;
  keystore: EncryptedKeystore;
}> {
  const keyPair = await crypto.subtle.generateKey(
    { name: "ECDH", namedCurve: "P-256" },
    true,
    ["deriveKey", "deriveBits"]
  );

  const rawPublicKey = await crypto.subtle.exportKey("raw", keyPair.publicKey);
  const publicKeyBase64 = bytesToBase64(new Uint8Array(rawPublicKey));

  const rawPrivateKey = await crypto.subtle.exportKey(
    "pkcs8",
    keyPair.privateKey
  );

  const salt = generateRandomBytes(16);
  const iv = generateRandomBytes(12);
  const aesKey = await derivePasswordKey(password, salt);

  const ciphertextBuffer = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv as BufferSource },
    aesKey,
    rawPrivateKey as BufferSource
  );

  const keystore: EncryptedKeystore = {
    salt: bytesToBase64(salt),
    iv: bytesToBase64(iv),
    ciphertext: bytesToBase64(new Uint8Array(ciphertextBuffer)),
    publicKey: publicKeyBase64,
  };

  return { publicKeyBase64, keystore };
}

/**
 * Decrypts a P-256 private key from a keystore using the password.
 */
export async function decryptEscrowPrivateKey(
  keystore: EncryptedKeystore,
  password: string
): Promise<CryptoKey> {
  const salt = base64ToBytes(keystore.salt);
  const iv = base64ToBytes(keystore.iv);
  const ciphertext = base64ToBytes(keystore.ciphertext);

  const aesKey = await derivePasswordKey(password, salt);

  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: iv as BufferSource },
    aesKey,
    ciphertext as BufferSource
  );

  return crypto.subtle.importKey(
    "pkcs8",
    new Uint8Array(decryptedBuffer),
    { name: "ECDH", namedCurve: "P-256" },
    false,
    ["deriveKey", "deriveBits"]
  );
}

/**
 * Encrypts data under a P-256 public key using ECIES.
 * Ephemeral P-256 key pair is generated, ECDH derived bits are hashed to produce an AES-GCM key,
 * and the payload is encrypted.
 * Returns a base64 string containing: ephemeralPublicKey (65 bytes) | IV (12 bytes) | ciphertext.
 */
export async function encryptECIES(
  data: Uint8Array,
  adviserPublicKeyBase64: string
): Promise<string> {
  const adviserPublicKeyBytes = base64ToBytes(adviserPublicKeyBase64);
  const adviserPublicKey = await crypto.subtle.importKey(
    "raw",
    adviserPublicKeyBytes as BufferSource,
    { name: "ECDH", namedCurve: "P-256" },
    false,
    []
  );

  const ephemeralKeyPair = await crypto.subtle.generateKey(
    { name: "ECDH", namedCurve: "P-256" },
    true,
    ["deriveBits"]
  );

  const sharedSecretBuffer = await crypto.subtle.deriveBits(
    {
      name: "ECDH",
      public: adviserPublicKey,
    },
    ephemeralKeyPair.privateKey,
    256
  );

  // Derive an AES-GCM key from the SHA-256 hash of the shared secret
  const sharedSecretHash = await crypto.subtle.digest(
    "SHA-256",
    sharedSecretBuffer
  );
  const aesKey = await crypto.subtle.importKey(
    "raw",
    sharedSecretHash,
    { name: "AES-GCM" },
    false,
    ["encrypt"]
  );

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertextBuffer = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv as BufferSource },
    aesKey,
    data as BufferSource
  );

  const rawEphemeralPublic = await crypto.subtle.exportKey(
    "raw",
    ephemeralKeyPair.publicKey
  );
  const ephemeralPublicBytes = new Uint8Array(rawEphemeralPublic);
  const ciphertextBytes = new Uint8Array(ciphertextBuffer);

  const result = new Uint8Array(
    ephemeralPublicBytes.length + iv.length + ciphertextBytes.length
  );
  result.set(ephemeralPublicBytes, 0);
  result.set(iv, ephemeralPublicBytes.length);
  result.set(ciphertextBytes, ephemeralPublicBytes.length + iv.length);

  return bytesToBase64(result);
}

/**
 * Decrypts ECIES data using the Adviser's private key.
 */
export async function decryptECIES(
  encryptedBase64: string,
  adviserPrivateKey: CryptoKey
): Promise<Uint8Array> {
  const combined = base64ToBytes(encryptedBase64);
  if (combined.length < 65 + 12) {
    throw new Error("Invalid ECIES packet size");
  }

  const ephemeralPublicBytes = combined.slice(0, 65);
  const iv = combined.slice(65, 65 + 12);
  const ciphertext = combined.slice(65 + 12);

  const ephemeralPublicKey = await crypto.subtle.importKey(
    "raw",
    ephemeralPublicBytes as BufferSource,
    { name: "ECDH", namedCurve: "P-256" },
    false,
    []
  );

  const sharedSecretBuffer = await crypto.subtle.deriveBits(
    {
      name: "ECDH",
      public: ephemeralPublicKey,
    },
    adviserPrivateKey,
    256
  );

  const sharedSecretHash = await crypto.subtle.digest(
    "SHA-256",
    sharedSecretBuffer
  );
  const aesKey = await crypto.subtle.importKey(
    "raw",
    sharedSecretHash,
    { name: "AES-GCM" },
    false,
    ["decrypt"]
  );

  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: iv as BufferSource },
    aesKey,
    ciphertext as BufferSource
  );

  return new Uint8Array(decryptedBuffer);
}

/**
 * Digitally signs a legal attestation statement using ECDSA P-256.
 */
export async function signAttestation(
  keystore: EncryptedKeystore,
  password: string,
  statement: string
): Promise<string> {
  const salt = base64ToBytes(keystore.salt);
  const iv = base64ToBytes(keystore.iv);
  const ciphertext = base64ToBytes(keystore.ciphertext);

  const aesKey = await derivePasswordKey(password, salt);

  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: iv as BufferSource },
    aesKey,
    ciphertext as BufferSource
  );

  const signingKey = await crypto.subtle.importKey(
    "pkcs8",
    new Uint8Array(decryptedBuffer),
    { name: "ECDSA", namedCurve: "P-256" },
    false,
    ["sign"]
  );

  const signatureBuffer = await crypto.subtle.sign(
    { name: "ECDSA", hash: { name: "SHA-256" } },
    signingKey,
    new TextEncoder().encode(statement)
  );

  return bytesToBase64(new Uint8Array(signatureBuffer));
}
